/* eslint camelcase: 0 */
import { DateTime } from 'luxon';
import { decamelizeKeys } from '../../lib';
import {
    BillingsFilterCountsSnapshot,
    EditRentalBillingSnapshot,
    NewRentalBillingSnapshot,
    RentalBillingSummarySnapshot,
    RentalBillingInput,
    RentalResultWithDestination,
    BillingPaginatedResponse,
    BillingFilterDateTrunc,
} from '../../models/RentalPayment';
import api, { rawClient } from '../api';
import { getBackVersion } from '../../config';
import { RemovedOrCanceledBillingProps } from '../../bound/rental/external';
import { PaginatedRequest } from '../../lib/pagination';
import { BillingStatuses } from '../../constants/rental';
import qs from 'query-string';
import { ExportFormat } from '../../constants/file';
import { RentalBillingChartsSnapshot } from '../../models/RentalPayment/Billing/BillingsCharts';

const getContractBillings = (
    contractId: string
): Promise<RentalResultWithDestination<RentalBillingInput[]>> =>
    api.request<RentalResultWithDestination<RentalBillingInput[]>>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings`,
    });

const getContractBilling = (
    billingId: string,
    contractId: string
): Promise<RentalResultWithDestination<RentalBillingInput[]>> =>
    api.request<RentalResultWithDestination<RentalBillingInput[]>>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}`,
    });

const getBillingSummary = (
    billingId: string,
    contractId: string
): Promise<RentalBillingSummarySnapshot> =>
    api.request<RentalBillingSummarySnapshot>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/summary`,
    });

const getRemovedOrCanceledBillings = (
    contractId: string,
    billingId: string
): Promise<RemovedOrCanceledBillingProps> =>
    api.request<RemovedOrCanceledBillingProps>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/removed_or_canceled`,
    });

const createBilling = (
    contractId: string,
    billing: NewRentalBillingSnapshot
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings`,
        data: decamelizeKeys(billing),
    });

const updateBilling = (
    contractId: string,
    billingId: string,
    billing: EditRentalBillingSnapshot
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}`,
        data: decamelizeKeys(billing),
    });

const removeBilling = (contractId: string, billingId: string): Promise<void> =>
    api.request<void>({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}`,
        params: { action: 'remove' },
    });

const archiveBilling = (
    contractId: string,
    billingId: string,
    action: 'archive' | 'unarchive'
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'DELETE',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}`,
        params: { action },
    });

const cancelBilling = (
    contractId: string,
    billingId: string
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/status`,
        data: { derived_status: 'canceled' },
    });

const settleBilling = (
    contractId: string,
    billingId: string,
    paidAt: DateTime
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'PATCH',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/status`,
        data: {
            derived_status: 'paid',
            paid_at: paidAt,
        },
    });

const downloadNotificationPdf = (
    contractId: string,
    billingId: string
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/notification_pdf`,
    });

const downloadReceiptPdf = (
    contractId: string,
    billingId: string
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/receipt_pdf`,
    });

const downloadNegotiationReceiptPdf = (
    contractId: string,
    billingId: string
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/negotiation_receipt_pdf`,
    });

const issueInvoice = (
    contractId: string,
    billingId: string,
    dueOnNextBusinessDay: boolean = false
): Promise<RentalBillingInput> =>
    api.request<RentalBillingInput>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/invoices`,
        data: {
            due_on_next_business_day: dueOnNextBusinessDay,
        },
    });

const downloadInvoicePdf = (
    contractId: string,
    billingId: string
): Promise<void> => {
    return rawClient.download({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/pdf`,
    });
};

const downloadTenantPaymentSlipPdf = (
    contractId: string,
    billingId: string
): Promise<void> =>
    rawClient.download({
        url: `${getBackVersion()}/rental/${contractId}/billings/${billingId}/pdf`,
    });

type BillingRequestFilters = {
    landlord?: string;
    address?: string;
    tenant?: string;
    status?: BillingStatuses[];
    isArchived?: boolean;
    minDate?: string;
    maxDate?: string;
    dateTrunc: BillingFilterDateTrunc;
};

const getPaginatedBillings = (
    params: PaginatedRequest & BillingRequestFilters
): Promise<BillingPaginatedResponse> => {
    return api.request<BillingPaginatedResponse>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/billings_page`,
        params: decamelizeKeys({ ...params, paginate: true }),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};

const downloadBillingsReport = (
    extension: ExportFormat,
    filters: BillingRequestFilters
): Promise<void> => {
    return rawClient.download({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/billings_page/${extension}`,
        params: decamelizeKeys(filters),
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'none' });
        },
    });
};

const getBillingsChartsDataPoints = (
    minDate?: DateTime,
    maxDate?: DateTime
): Promise<RentalBillingChartsSnapshot[]> => {
    return api.request<RentalBillingChartsSnapshot[]>({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/billings_page/charts`,
        params: {
            max_date: maxDate?.toISODate(),
            min_date: minDate?.toISODate(),
        },
    });
};

const getBillingFiltersCounts = (
    minDueDate: DateTime | undefined,
    maxDueDate: DateTime | undefined
): Promise<BillingsFilterCountsSnapshot> => {
    return api.request({
        method: 'GET',
        url: `${getBackVersion()}/agent/rental/contracts_rental/total/billing_counts`,
        params: {
            min_due_date: minDueDate?.toISODate(),
            max_due_date: maxDueDate?.toISODate(),
        },
    });
};

const duplicateBilling = (
    billingId: string,
    contractId: string
): Promise<RentalBillingInput> => {
    return api.request<RentalBillingInput>({
        method: 'POST',
        url: `${getBackVersion()}/agent/rental/contracts_rental/${contractId}/billings/${billingId}/duplicate`,
    });
};

export {
    archiveBilling,
    cancelBilling,
    createBilling,
    downloadNotificationPdf,
    downloadReceiptPdf,
    getBillingSummary,
    getContractBilling,
    getContractBillings,
    downloadInvoicePdf,
    downloadNegotiationReceiptPdf,
    issueInvoice,
    removeBilling,
    settleBilling,
    updateBilling,
    downloadTenantPaymentSlipPdf,
    getRemovedOrCanceledBillings,
    getPaginatedBillings,
    getBillingFiltersCounts,
    downloadBillingsReport,
    getBillingsChartsDataPoints,
    duplicateBilling,
};
